import { Dialog, Transition, Combobox } from '@headlessui/react';
import clsx from 'clsx';
import { Fragment } from 'react';

import { ProjectGroup } from '@/api/project';
import { ReactComponent as CloseIcon } from '@/assets/icons/bx-x.svg';
import { Button } from '@/components/Elements/Button';
import { Input } from '@/components/Elements/Input';

import { projectCopyInformation } from '../../interfaces/projectInterface';

interface CopyModaProps {
  item: projectCopyInformation;
  projectGroups: Array<ProjectGroup>;
  setItem: React.Dispatch<React.SetStateAction<projectCopyInformation>>;
  handleSubmit: () => void;
}

export const CopyModal = (props: CopyModaProps) => {
  const handleClose = () => {
    props.setItem((state) => {
      return {
        ...state,
        isOpen: false,
      };
    });
  };

  const getProjectNameError = () => {
    if (props.item.projectName == '') {
      return '案件名は必須項目です';
    } else if (props.item.projectName.length > 50) {
      return '案件名は50文字以下で入力してください';
    }
    return undefined;
  };
  return (
    <Transition.Root show={props.item.isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        onClose={() => {
          handleClose();
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-[#6B7280BF]/75 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5">
                <div className="w-[512px] rounded-lg bg-white">
                  <div className="flex flex-col p-0">
                    <div className="flex flex-row items-center">
                      <div className="flex w-full flex-col">
                        <div className="flex pt-4 text-center">
                          <span className="text-lg font-medium leading-6">案件を複製しますか？</span>
                          <div className="ml-auto">
                            <CloseIcon
                              width={24}
                              height={24}
                              fill="#9CA3AF"
                              onClick={() => {
                                handleClose();
                              }}
                            />
                          </div>
                        </div>
                        <div className="pt-6 text-left">
                          <span className="whitespace-pre-wrap text-sm font-normal leading-5 text-gray-500">
                            {`複製後は案件の削除ができません。\nプロジェクト名・案件名を入力してください。`}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex-none">
                    <div className="gap-2 pt-6">
                      <Combobox value={props.item.projectGroupName}>
                        <span className="flex">
                          <span className={clsx('block  pb-2 text-sm font-semibold text-gray-700')}>
                            プロジェクト名
                          </span>
                        </span>
                        <Combobox.Input
                          className="flex h-10 w-full items-center justify-center rounded-md border-[1px] border-gray-300 font-medium placeholder:text-sm placeholder:text-gray-400 hover:opacity-80 focus:outline-none disabled:cursor-not-allowed disabled:opacity-70"
                          placeholder="例：商品名＿インフルエンサー施策"
                          value={props.item.projectGroupName || ''}
                          onChange={(e: any) => {
                            props.setItem((state) => {
                              return {
                                ...state,
                                projectGroupName: e.target.value,
                                projectGroupId: null,
                              };
                            });
                          }}
                        />
                        <Combobox.Options className="scroll-py-2 overflow-y-auto  rounded-lg border border-gray-300 text-sm text-gray-800">
                          {props.projectGroups
                            .filter((group) => {
                              if (
                                group.name != null &&
                                props.item.projectGroupName != null &&
                                props.item.projectGroupName != ''
                              ) {
                                return group.name.indexOf(props.item.projectGroupName) > -1;
                              }
                            })
                            .map((group) => (
                              <Combobox.Option
                                key={group.projectGroupId}
                                value={group.name}
                                className={({ active }) =>
                                  clsx(
                                    'cursor-default select-none px-4 py-2',
                                    active &&
                                      'bg-white text-sm font-normal leading-5 text-gray-900  hover:bg-gray-300 hover:font-semibold'
                                  )
                                }
                                onClick={() => {
                                  props.setItem((state) => {
                                    return {
                                      ...state,
                                      projectGroupId: group.projectGroupId,
                                      projectGroupName: group.name,
                                    };
                                  });
                                }}
                              >
                                {group.name}
                              </Combobox.Option>
                            ))}
                        </Combobox.Options>
                        {props.item.projectGroupName && props.item.projectGroupName.length > 50 && (
                          <p className="text-left text-red-500">プロジェクト名は50文字以下で入力してください</p>
                        )}
                        <div className="flex w-[464px] pt-2">
                          <span className="text-left text-xs font-medium leading-5 text-gray-500">
                            ※作成した案件を管理する、親となるプロジェクトです。複数案件を紐づけて管理することができます。
                          </span>
                        </div>
                      </Combobox>
                    </div>
                    <div className="pt-6">
                      <Input
                        id="name"
                        type="text"
                        placeholder="例：25年XX月＿商品名＿インフルエンサー施策"
                        label="案件名"
                        className="w-[464px] placeholder:text-sm"
                        classnamelabel="pb-2"
                        classNameRequiredText="ml-[2px] mt-[3px] font-bold text-[10px] text-gray-700"
                        placeholderColor="placeholder:text-gray-400"
                        isPlaceHolderRight={false}
                        value={props.item.projectName}
                        onChange={(e: any) => {
                          props.setItem((state) => {
                            return {
                              ...state,
                              projectName: e.target.value,
                            };
                          });
                        }}
                      ></Input>
                      {getProjectNameError() && <p className="text-left text-red-500">{getProjectNameError()}</p>}
                    </div>
                  </div>
                  <div className="pl-6 pt-6">
                    <div className="flex flex-row items-center justify-end gap-3 p-0">
                      <Button
                        variant="white"
                        size="md"
                        onClick={() => {
                          handleClose();
                        }}
                      >
                        キャンセル
                      </Button>
                      <Button
                        variant="primary"
                        size="md"
                        onClick={async () => {
                          props.handleSubmit();
                          handleClose();
                        }}
                      >
                        複製を確定
                      </Button>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
